<script setup>
import { computed, onMounted, ref } from 'vue';
import { endOfDay, getUnixTime, startOfDay, subDays } from 'date-fns';
import { useI18n } from 'vue-i18n';
import ReportsAPI from 'dashboard/api/reports';
import { downloadCsvFile } from 'dashboard/helper/downloadHelper';
import ReportHeader from './components/ReportHeader.vue';
import SearchDateRangeSelector from 'dashboard/modules/search/components/SearchDateRangeSelector.vue';
import SearchInboxSelector from 'dashboard/modules/search/components/SearchInboxSelector.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import { useStore } from 'dashboard/composables/store';

const { t, locale } = useI18n();
const store = useStore();

const filters = ref({
  inboxId: null,
  dateRange: {
    type: 'last_30_days',
    from: getUnixTime(startOfDay(subDays(new Date(), 30))),
    to: getUnixTime(endOfDay(new Date())),
  },
});

const isLoading = ref(false);
const isExporting = ref(false);
const report = ref({
  totalCount: 0,
  totalConversationsCount: 0,
  totalTemplatesCount: 0,
  templates: [],
  categories: [],
  rows: [],
});

const localeCode = computed(() => locale.value?.replace('_', '-') || 'es');

const payload = computed(() => ({
  from: filters.value.dateRange?.from,
  to: filters.value.dateRange?.to,
  inboxId: filters.value.inboxId,
}));

const hasRows = computed(() => report.value.rows.length > 0);

const formatDateTime = value => {
  if (!value) return '--';

  return new Intl.DateTimeFormat(localeCode.value, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(value));
};

const sourceLabel = source =>
  t(`WHATSAPP_TEMPLATE_REPORTS.SOURCES.${source.toUpperCase()}`, source);

const fetchReport = async () => {
  try {
    isLoading.value = true;
    const { data } = await ReportsAPI.getWhatsappTemplateSummary(payload.value);
    report.value = {
      totalCount: data.total_count || 0,
      totalConversationsCount: data.total_conversations_count || 0,
      totalTemplatesCount: data.total_templates_count || 0,
      templates: data.templates || [],
      categories: data.categories || [],
      rows: data.rows || [],
    };
  } finally {
    isLoading.value = false;
  }
};

const exportReport = async () => {
  try {
    isExporting.value = true;
    const { data } = await ReportsAPI.downloadWhatsappTemplateSummary(
      payload.value
    );
    const timestamp = filters.value.dateRange?.to || getUnixTime(new Date());
    downloadCsvFile(`whatsapp-template-summary-${timestamp}.csv`, data);
  } finally {
    isExporting.value = false;
  }
};

onMounted(async () => {
  store.dispatch('inboxes/get');
  await fetchReport();
});
</script>

<template>
  <div class="flex flex-col gap-5 pb-8">
    <ReportHeader
      :header-title="$t('WHATSAPP_TEMPLATE_REPORTS.HEADER')"
      :header-description="$t('WHATSAPP_TEMPLATE_REPORTS.DESCRIPTION')"
    >
      <NextButton
        slate
        :label="$t('WHATSAPP_TEMPLATE_REPORTS.DOWNLOAD')"
        icon="i-lucide-download"
        :is-loading="isExporting"
        @click="exportReport"
      />
    </ReportHeader>

    <div
      class="chatmigo-report-filter-card rounded-xl border border-n-weak bg-n-alpha-1 p-4"
    >
      <div class="flex flex-col lg:flex-row items-start lg:items-center gap-3">
        <SearchDateRangeSelector
          v-model="filters.dateRange"
          class="min-w-0 max-w-full"
          @change="fetchReport"
        />
        <div class="w-px h-4 bg-n-weak flex-shrink-0 hidden lg:block" />
        <SearchInboxSelector
          v-model="filters.inboxId"
          :label="$t('WHATSAPP_TEMPLATE_REPORTS.INBOX_FILTER')"
          @change="fetchReport"
        />
      </div>
    </div>

    <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
      <div class="rounded-xl border border-n-weak bg-n-solid-1 p-4">
        <p class="mb-1 text-sm text-n-slate-11">
          {{ $t('WHATSAPP_TEMPLATE_REPORTS.TOTAL_MESSAGES') }}
        </p>
        <p class="mb-0 text-2xl font-semibold text-n-slate-12">
          {{ report.totalCount }}
        </p>
      </div>
      <div class="rounded-xl border border-n-weak bg-n-solid-1 p-4">
        <p class="mb-1 text-sm text-n-slate-11">
          {{ $t('WHATSAPP_TEMPLATE_REPORTS.TOTAL_CONVERSATIONS') }}
        </p>
        <p class="mb-0 text-2xl font-semibold text-n-slate-12">
          {{ report.totalConversationsCount }}
        </p>
      </div>
      <div class="rounded-xl border border-n-weak bg-n-solid-1 p-4">
        <p class="mb-1 text-sm text-n-slate-11">
          {{ $t('WHATSAPP_TEMPLATE_REPORTS.TOTAL_TEMPLATES') }}
        </p>
        <p class="mb-0 text-2xl font-semibold text-n-slate-12">
          {{ report.totalTemplatesCount }}
        </p>
      </div>
    </div>

    <div
      v-if="isLoading"
      class="chatmigo-report-empty-state flex flex-col items-center justify-center rounded-xl border border-dashed border-n-weak bg-n-alpha-1 px-6 py-10 text-center"
    >
      <p class="m-0 text-n-slate-11">
        {{ $t('WHATSAPP_TEMPLATE_REPORTS.LOADING') }}
      </p>
    </div>

    <div
      v-else-if="!hasRows"
      class="chatmigo-report-empty-state flex flex-col items-center justify-center rounded-xl border border-dashed border-n-weak bg-n-alpha-1 px-6 py-10 text-center"
    >
      <p class="m-0 text-n-slate-11">
        {{ $t('WHATSAPP_TEMPLATE_REPORTS.EMPTY_STATE') }}
      </p>
    </div>

    <template v-else>
      <section class="grid grid-cols-1 xl:grid-cols-2 gap-5">
        <div class="rounded-xl border border-n-weak bg-n-solid-1 p-4">
          <h3 class="mb-4 text-base font-semibold text-n-slate-12">
            {{ $t('WHATSAPP_TEMPLATE_REPORTS.BY_TEMPLATE') }}
          </h3>
          <div class="overflow-x-auto">
            <table class="w-full text-sm">
              <thead>
                <tr class="text-left text-n-slate-11 border-b border-n-weak">
                  <th class="py-2 pr-3">
                    {{ $t('WHATSAPP_TEMPLATE_REPORTS.COLUMNS.TEMPLATE') }}
                  </th>
                  <th class="py-2 pr-3">
                    {{ $t('WHATSAPP_TEMPLATE_REPORTS.COLUMNS.CATEGORY') }}
                  </th>
                  <th class="py-2 pr-3">
                    {{ $t('WHATSAPP_TEMPLATE_REPORTS.COLUMNS.LANGUAGE') }}
                  </th>
                  <th class="py-2 pr-3">
                    {{ $t('WHATSAPP_TEMPLATE_REPORTS.COLUMNS.COUNT') }}
                  </th>
                  <th class="py-2">
                    {{ $t('WHATSAPP_TEMPLATE_REPORTS.COLUMNS.CONVERSATIONS') }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="row in report.templates"
                  :key="`${row.template_name}-${row.template_language}-${row.template_category}`"
                  class="border-b border-n-weak last:border-b-0"
                >
                  <td class="py-2 pr-3 text-n-slate-12">
                    {{ row.template_name }}
                  </td>
                  <td class="py-2 pr-3 text-n-slate-12">
                    {{ row.template_category }}
                  </td>
                  <td class="py-2 pr-3 text-n-slate-12">
                    {{ row.template_language }}
                  </td>
                  <td class="py-2 pr-3 text-n-slate-12">
                    {{ row.total_count }}
                  </td>
                  <td class="py-2 text-n-slate-12">
                    {{ row.conversations_count }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div class="rounded-xl border border-n-weak bg-n-solid-1 p-4">
          <h3 class="mb-4 text-base font-semibold text-n-slate-12">
            {{ $t('WHATSAPP_TEMPLATE_REPORTS.BY_CATEGORY') }}
          </h3>
          <div class="overflow-x-auto">
            <table class="w-full text-sm">
              <thead>
                <tr class="text-left text-n-slate-11 border-b border-n-weak">
                  <th class="py-2 pr-3">
                    {{ $t('WHATSAPP_TEMPLATE_REPORTS.COLUMNS.CATEGORY') }}
                  </th>
                  <th class="py-2">
                    {{ $t('WHATSAPP_TEMPLATE_REPORTS.COLUMNS.COUNT') }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="row in report.categories"
                  :key="row.category_name"
                  class="border-b border-n-weak last:border-b-0"
                >
                  <td class="py-2 pr-3 text-n-slate-12">
                    {{ row.category_name }}
                  </td>
                  <td class="py-2 text-n-slate-12">{{ row.total_count }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <section class="rounded-xl border border-n-weak bg-n-solid-1 p-4">
        <h3 class="mb-4 text-base font-semibold text-n-slate-12">
          {{ $t('WHATSAPP_TEMPLATE_REPORTS.RECENT_ACTIVITY') }}
        </h3>
        <div class="overflow-x-auto">
          <table class="w-full text-sm">
            <thead>
              <tr class="text-left text-n-slate-11 border-b border-n-weak">
                <th class="py-2 pr-3">
                  {{ $t('WHATSAPP_TEMPLATE_REPORTS.COLUMNS.DATE') }}
                </th>
                <th class="py-2 pr-3">
                  {{ $t('WHATSAPP_TEMPLATE_REPORTS.COLUMNS.CONVERSATION') }}
                </th>
                <th class="py-2 pr-3">
                  {{ $t('WHATSAPP_TEMPLATE_REPORTS.COLUMNS.INBOX') }}
                </th>
                <th class="py-2 pr-3">
                  {{ $t('WHATSAPP_TEMPLATE_REPORTS.COLUMNS.TEMPLATE') }}
                </th>
                <th class="py-2 pr-3">
                  {{ $t('WHATSAPP_TEMPLATE_REPORTS.COLUMNS.SOURCE') }}
                </th>
                <th class="py-2">
                  {{ $t('WHATSAPP_TEMPLATE_REPORTS.COLUMNS.SENDER') }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="row in report.rows"
                :key="row.id"
                class="border-b border-n-weak last:border-b-0"
              >
                <td class="py-2 pr-3 text-n-slate-12">
                  {{ formatDateTime(row.sent_at) }}
                </td>
                <td class="py-2 pr-3 text-n-slate-12">
                  #{{ row.conversation_display_id }}
                </td>
                <td class="py-2 pr-3 text-n-slate-12">{{ row.inbox_name }}</td>
                <td class="py-2 pr-3 text-n-slate-12">
                  {{ row.template_name }}
                </td>
                <td class="py-2 pr-3 text-n-slate-12">
                  {{ sourceLabel(row.source_type) }}
                </td>
                <td class="py-2 text-n-slate-12">{{ row.sender_name }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
    </template>
  </div>
</template>
